import React from 'react';
import range from 'lodash/range';

import TimeField from 'components/pure/utils/form/TimeField';
import {DateOptionsArr, DayOfWeek} from '@store/@types/util/time/DayOfWeek';
import ordinalSuffix from 'utils/oridinalSuffix';
import {makeStyles} from '@components/@theme';
import {Int} from '@store/@types/util';
import SingleSelect, {SingleSelectItem} from '@components/single-select';

import {AssignmentFrequency, KpiTarget, periodIsShorter} from '@store/@types/kpi';
import Time from 'records/Time';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';

interface Props {
  value: Partial<{
    frequency: AssignmentFrequency;
    timeOfDay: Time;
    dayOfMonth: Int;
    dayOfWeek: DayOfWeek;
  }>;
  onChange: (newValue: any) => void;
  kpiTarget: KpiTarget;
}

export const FrequencyField: React.FC<Props> = ({onChange, value, kpiTarget}) => {
  const {at, label, progress, progressDay} = useStyles();

  const monthly = value.frequency === AssignmentFrequency.MONTHLY && (
    <div className={progressDay}>
      <div className={label}>on the</div>
      <SingleSelect
        MenuProps={{
          style: {maxHeight: 400, overflowY: scroll},
          anchorOrigin: 'bottom',
        }}
        width={60}
        onChange={e => onChange({dayOfMonth: e.target.value as Int})}
        value={value.dayOfMonth}>
        {range(1, 24).map(n => (
          <SingleSelectItem key={n} value={n}>
            {ordinalSuffix(n)}
          </SingleSelectItem>
        ))}
      </SingleSelect>
      <div className={label}>business day of the month</div>
    </div>
  );

  const weekly = value.frequency === AssignmentFrequency.WEEKLY && (
    <div className={progressDay}>
      <div className={label}>on</div>
      <SingleSelect
        width={125}
        onChange={e => onChange({dayOfWeek: e.target.value as DayOfWeek})}
        value={value.dayOfWeek}>
        {DateOptionsArr.map(({label, value}) => (
          <SingleSelectItem value={value}>{label}</SingleSelectItem>
        ))}
      </SingleSelect>
    </div>
  );

  function changeFrequency(frequency: AssignmentFrequency) {
    switch (frequency) {
      case AssignmentFrequency.DAILY:
        return onChange({frequency, dayOfMonth: undefined, dayOfWeek: undefined});
      case AssignmentFrequency.WEEKLY:
        return onChange({frequency, dayOfMonth: undefined});
      case AssignmentFrequency.MONTHLY:
        return onChange({frequency, dayOfWeek: undefined});
    }
  }

  const options = Object.values(AssignmentFrequency).map(freq => {
    if (!periodIsShorter(freq, kpiTarget.frequency)) {
      return (
        <GenericTooltip
          key={freq}
          positionRight
          tooltip={`KPI frequency is ${kpiTarget.frequency.toLowerCase()}`}>
          <SingleSelectItem disabled value={freq}>
            {freq.toLowerCase()}
          </SingleSelectItem>
        </GenericTooltip>
      );
    }

    return (
      <SingleSelectItem key={freq} value={freq}>
        {freq.toLowerCase()}
      </SingleSelectItem>
    );
  });

  return (
    <div className={progress}>
      <SingleSelect
        style={{width: 100}}
        value={value.frequency}
        onChange={e => changeFrequency(e.target.value as AssignmentFrequency)}>
        {options}
      </SingleSelect>
      {weekly}
      {monthly}
      <div className={at}>at</div>
      <TimeField value={value.timeOfDay!} onChange={time => onChange({timeOfDay: time})} />
    </div>
  );
};

const useStyles = makeStyles(() => ({
  at: {
    margin: '0 8px',
  },
  label: {},
  progress: {
    display: 'flex',
    alignItems: 'center',
  },
  progressDay: {
    display: 'flex',
    alignItems: 'center',
    '& > $label': {
      margin: '0 8px',
    },
  },
}));
